<template>
    <div class="row mt-4">
        <div class="col-12 col-md-9 col-lg-8 col-xl-6 mr-3">
            <ValidationObserver v-slot="{ handleSubmit, invalid }" ref="customMedicationForm">
                <form role="form" @submit.prevent="handleSubmit(submit)">
                    <div class="form-group mb-4">
                        <div class="d-flex flex-row">
                            <div class="d-flex flex-column mr-4">
                                <span>
                                    *<i> {{ $t('field_required') }}</i>
                                </span>
                            </div>
                        </div>
                    </div>
                    <!-- VNR -->
                    <div class="form-group">
                        <ValidationProvider
                            name="validation_vnr"
                            :rules="{
                                custom_vnr_valid: null,
                                length: customVnrLength,
                                required: true
                            }"
                            v-slot="{ errors }"
                        >
                            <label for="vnr">{{ $t('vnr') }} *</label>
                            <input
                                class="form-control"
                                id="vnr"
                                type="text"
                                v-model="customMedication.vnr"
                                :disabled="edit"
                                :placeholder="$t('custom_medication_vnr_tooltip')"
                            />
                            <div class="invalid-feedback" style="display: block">
                                <span>
                                    {{ $t(errors[0]) }}
                                    {{ errors[0] === 'error_wrong_length' ? customVnrLength : null }}
                                </span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <!-- NAME -->
                    <div class="form-group">
                        <ValidationProvider name="validation_name" rules="required" v-slot="{ errors }">
                            <label for="name">{{ $t('name') }} *</label>
                            <input class="form-control" id="name" type="text" v-model="customMedication.name" />
                            <div class="invalid-feedback" style="display: block">
                                <span>{{ $t(errors[0]) }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <!-- MARKETING AUTHORIZATION HOLDER (COMPANY) -->
                    <div class="form-group">
                        <ValidationProvider name="validation_company" rules="required" v-slot="{ errors }">
                            <label for="company">{{ $t('marketing_authorization_holder') }} *</label>
                            <input class="form-control" id="company" type="text" v-model="customMedication.company" />
                            <div class="invalid-feedback" style="display: block">
                                <span>{{ $t(errors[0]) }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <!-- SUPPLIER -->
                    <div class="form-group">
                        <ValidationProvider name="validation_supplier" rules="required" v-slot="{ errors }">
                            <label for="supplier">{{ $t('supplier') }} *</label>
                            <input class="form-control" id="supplier" type="text" v-model="customMedication.supplier" />
                            <div class="invalid-feedback" style="display: block">
                                <span>{{ $t(errors[0]) }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <!-- RESELLER -->
                    <div class="form-group">
                        <ValidationProvider name="validation_reseller" v-slot="{ errors }">
                            <label for="reseller">{{ $t('reseller') }}</label>
                            <input
                                class="form-control"
                                id="reseller"
                                type="text"
                                v-model="customMedication.company_reseller"
                            />
                            <div class="invalid-feedback" style="display: block">
                                <span>{{ $t(errors[0]) }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <div class="form-group d-flex flex-row justify-content-between">
                        <!-- DOSAGE -->
                        <div class="d-flex flex-column" :style="{ width: '48%' }">
                            <ValidationProvider name="validation_dosage" rules="required" v-slot="{ errors }">
                                <label for="dosage">{{ $t('dosage') }} *</label>
                                <input
                                    class="form-control"
                                    id="dosage"
                                    :placeholder="$t('dosage_tooltip')"
                                    type="text"
                                    v-model="customMedication.dosage"
                                />
                                <div class="invalid-feedback" style="display: block">
                                    <span>{{ $t(errors[0]) }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                        <!-- DOSAGE FORM -->
                        <div class="d-flex flex-column" :style="{ width: '48%' }">
                            <ValidationProvider name="validation_dosage_form" rules="required" v-slot="{ errors }">
                                <label for="dosage_form">{{ $t('dosage_form') }} *</label>
                                <input
                                    class="form-control"
                                    id="dosage_form"
                                    type="text"
                                    v-model="customMedication.dosage_form"
                                />
                                <div class="invalid-feedback" style="display: block">
                                    <span>{{ $t(errors[0]) }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="form-group">
                        <div class="d-flex flex-row">
                            <label for="package_size">{{ $t('package_size') }} *</label>
                        </div>
                        <div class="d-flex flex-row justify-content-between">
                            <!-- PACKAGE SIZE -->
                            <div class="d-flex flex-column" :style="{ width: '48%' }">
                                <ValidationProvider
                                    name="validation_package_size"
                                    :rules="{ integer: true, positive: true, required: true }"
                                    v-slot="{ errors }"
                                >
                                    <input
                                        class="form-control"
                                        id="package_size"
                                        :placeholder="$t('amount')"
                                        type="text"
                                        v-model="customMedication.package_size"
                                    />
                                    <div class="invalid-feedback" style="display: block">
                                        <span>{{ $t(errors[0]) }}</span>
                                    </div>
                                </ValidationProvider>
                            </div>
                            <!-- FORM -->
                            <div class="d-flex flex-column" :style="{ width: '48%' }">
                                <ValidationProvider name="validation_form" rules="required" v-slot="{ errors }">
                                    <input
                                        class="form-control"
                                        id="form"
                                        :placeholder="$t('form')"
                                        type="text"
                                        v-model="customMedication.form"
                                    />
                                    <div class="invalid-feedback" style="display: block">
                                        <span>{{ $t(errors[0]) }}</span>
                                    </div>
                                </ValidationProvider>
                            </div>
                        </div>
                    </div>
                    <!-- CHEMICAL -->
                    <div class="form-group">
                        <label for="chemical">{{ $t('chemical') }}</label>
                        <input class="form-control" id="chemical" type="text" v-model="customMedication.chemical" />
                    </div>
                    <!-- ATC -->
                    <div class="form-group">
                        <label for="atc">{{ $t('atc') }}</label>
                        <input class="form-control" id="atc" type="text" v-model="customMedication.atc" />
                    </div>
                    <!-- DRUG -->
                    <div class="form-group d-flex">
                        <label for="drugSwitch" class="mr-2">
                            {{ $t('drug') }}
                        </label>
                        <b-form-checkbox id="drugSwitch" v-model="customMedication.drug" />
                    </div>
                    <hr />
                    <!-- BUTTONS: SUBMIT, CANCEL -->
                    <div class="d-flex flex-row">
                        <div class="d-flex flex-column mr-3">
                            <input
                                type="submit"
                                class="btn btn-md btn-success"
                                :disabled="submitClicked || invalid"
                                :value="$t(submitButtonName)"
                            />
                        </div>
                        <div class="d-flex flex-column">
                            <input
                                type="button"
                                v-on:click="() => $router.push({ name: 'customMedications.index' })"
                                class="btn btn-secondary"
                                :value="$t('cancel')"
                                :disabled="submitClicked"
                            />
                        </div>
                    </div>
                </form>
            </ValidationObserver>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        customMedication: {
            type: Object,
            required: true
        },
        edit: {
            type: Boolean,
            required: false,
            default: false
        },
        submitButtonName: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            customVnrLength: 7,
            submitClicked: false
        };
    },
    created() {},
    methods: {
        // emit form submit action
        submit() {
            this.submitClicked = true;
            this.$emit('submit');
        }
    }
};
</script>
